import DragDrop from 'components/DragDrop/DragDrop';
import UserNotification from 'components/UserNotification/UserNotification';
import { __callbacks } from 'Utils/Callbacks/Callback';
import { __config } from 'Utils/Configs/Config';
import { AppState, ChatState } from 'Utils/Enums';
import { IFileDrop } from 'Utils/Interfaces';
import { __style } from 'Utils/Styles/Style';
import { __window } from 'Utils/Window/Window';
import WindowArticle from 'components/WindowArticle/WindowArticle';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import './App.css';
import Body from './components/Body/Body';
import Header from './components/Header/Header';
import Loading from './components/Loading/Loading';
import { __app } from 'Utils/App';
import { AppContext, ChatContext, ConfigContext } from 'Utils/Contexts';
import Pusher from 'pusher-js';
import { PusherProvider } from "@harelpls/use-pusher";
import { __sb } from './Utils/SB/sb';

function App() {
    // contexts ---------
    const [appStateContext, setAppStateContext] = useState(AppState.home);
    const [chatStateContext, setChatStateContext] = useState(ChatState.newConversation);
    const [configContext, setConfigContext] = useState(null);

    // variables --------
    const [isLoading, setIsLoading] = useState(true);
    const [classHeightHeader, setClassHeightHeader] = useState("height-header-home");
    const [classHeaderHide, setClassHeaderHide] = useState("hide-header-content");
    const [userNotification, setUserNotification] = useState<ReactNode | null>(null);

    const config = {
        clientKey: "7b2eee4c1e21137a3721",
        cluster: "us3",
    };

    const initListeners = useCallback(() => {
        __config.subscribe((data: any) => {
            if (data.status !== "finished") return;
            handleOnConfigFinish(data);
        });

        document.addEventListener("paste", (e: ClipboardEvent) => {
            if (!e.clipboardData) return;
            if (!e.clipboardData.files[0]) return;

            const fileDrop: IFileDrop = {
                appState: AppState.chat,
                file: e.clipboardData.files[0]
            };
            __callbacks.fileUpload.trigger(fileDrop.file);
        });

        if (window.addEventListener) {
            window.addEventListener("message", onMessage, false);
        }

        __callbacks.onButtonBackClick(() => {
            setAppStateContext(AppState.home);
        });
        __callbacks.onSeeAllConversationsClick(() => {
            setAppStateContext(AppState.allConversations);
        });
        __callbacks.onUpdateAppState((data: any) => {
            setAppStateContext(data.appState);
        });
        __callbacks.onSetChatHeaderSmall(() => {
            setClassHeightHeader("height-header-chat-small");
        });
        __callbacks.onChatState((data: any) => {
            setChatStateContext(data.chatState.conversationId);
        });
        __callbacks.onStartNewChat(() => {
            setAppStateContext(AppState.chat);
            setChatStateContext(ChatState.newConversation);
            __callbacks.chatSendButton.trigger({ enable: false });
            __callbacks.resetChatInputs.trigger(true);
        });
        __callbacks.onContinueConversation((data: any) => {
            setAppStateContext(AppState.chat);
            setChatStateContext(ChatState.continueConversation);
            __callbacks.chatSendButton.trigger({ enable: false });
            __callbacks.resetChatInputs.trigger(true);
        });
        __callbacks.onUserNotification((text: any) => {
            setUserNotification(<UserNotification>{text}</UserNotification>);
        });
        __callbacks.onUserNotificationClose(() => {
            setUserNotification(null);
        });

        // Add a new callback to update conversation
        __callbacks.onConversation((data: any) => {
            setChatStateContext(data.chatState);
        });
    }, []);

    const onMessage = (event: any) => {
        var data = event.data;
        if (data.func === 'setStatus') {
            localStorage.setItem('widget_status' + window.location.search.substr(1).split("&")[0].split("=")[1], data.message);
        }
    }

    useEffect(() => {
        __window.initWindowVariables();
        initListeners();

        setTimeout(() => {
            setClassHeaderHide("");
            __sb.loadMissedChats();
        }, 800);
    }, [initListeners]);

    const handleOnConfigFinish = (data: any) => {
        __window.handleOnConfigFinish(data);
        __style.initStyles(data.config);
        setIsLoading(false);
        setConfigContext(data.config);
    }

    const onDragOver = (e: any) => {
        e.stopPropagation();
        e.preventDefault();
        __callbacks.fileDragOver.trigger({});
    }

    const onDrop = (e: any) => {
        e.stopPropagation();
        e.preventDefault();
        __callbacks.fileDrop.trigger({
            appState: appStateContext,
            file: e.dataTransfer.files[0]
        });
    }

    useEffect(() => {
        if (appStateContext === AppState.home) {
            return setClassHeightHeader(__app.getClassHeightHeader(appStateContext));
        } else if (chatStateContext === ChatState.continueConversation) {
            return setClassHeightHeader("height-header-chat-small");
        }
        setClassHeightHeader(__app.getClassHeightHeader(appStateContext));
    }, [appStateContext, chatStateContext]);

    const getMainUI = () => {
        if (isLoading) {
            return <div className="container-loading">
                <Loading {...{ color1: "black", color2: "#4a4a4a" }}></Loading>
            </div>
        } else {
            return <div className="container-body">
                <div className={`container-header config-header-backgroundColor ${classHeightHeader}`}>
                    <Header></Header>
                </div>
                <div onDragOver={onDragOver} onDrop={onDrop} className="container-content config-app-body-backgroundColor">
                    <Body></Body>
                    <DragDrop></DragDrop>
                </div>
                <div className="container-window-article">
                    <WindowArticle></WindowArticle>
                </div>
                <div className="container-user-notification">
                    {userNotification}
                </div>
            </div>
        }
    }

    return (
        <PusherProvider {...config}>
            <div className="container-wrapper config-app-body-backgroundColor">
                <div className={`container ${classHeaderHide}`}>
                    <AppContext.Provider value={appStateContext}>
                        <ChatContext.Provider value={chatStateContext}>
                            <ConfigContext.Provider value={configContext}>
                                {getMainUI()}
                            </ConfigContext.Provider>
                        </ChatContext.Provider>
                    </AppContext.Provider>
                </div>
            </div>
        </PusherProvider>
    );
}

export default App;
